import React, { type FC, forwardRef, useEffect } from "react";
import { useCustomTranslation } from "clientState/AuthFlow/hooks";
import {
  useModalActions,
  useModalState,
} from "clientState/AuthFlow/providers/ModalScreenProvider";
import {
  screenNames,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { Trans } from "@edreamsodigeo/frontend-core";
import {
  Body,
  Box,
  Button,
  Flex,
  Glyph,
  Heading,
  SystemModalContent,
} from "@edreamsodigeo/prisma-design-system";

import { CloseHeader } from "../Header";
import { MODAL_STYLES } from "../Helpers/ModalStyles";
import { type SuccessBodyProps } from "./contract";
import SuccessBodyEmail from "./SuccessBodyEmail";

const SuccessBody: FC<SuccessBodyProps> = forwardRef((props, ref) => {
  const { state } = useScreenState();
  const { email } = state.userData || {};
  const { customTranslation } = useCustomTranslation();
  const { isCloseable, isSuccessCloseable } = useModalState();
  const { closeModal, allowCloseableModal, preventCloseableModal } =
    useModalActions();
  const { titleColor } = useModalState();

  const { content: title } = customTranslation({
    customKey: "authentication.emailSentSuccess.title",
    defaultKey: "authentication.emailSentSuccess.title",
  });
  const { content: body } = customTranslation({
    customKey: "authentication.emailSentSuccess.secondMessage",
    defaultKey: "authentication.emailSentSuccess.secondMessage",
  });
  const { content: footer } = customTranslation({
    customKey: "authentication.emailSentSuccess.instructions",
    defaultKey: "authentication.emailSentSuccess.instructions",
  });
  const { content: buttonText } = customTranslation({
    customKey: "authentication.emailSentSuccess.button.ok",
    defaultKey: "authentication.emailSentSuccess.button.ok",
  });
  useEffect(() => {
    if (!isCloseable && isSuccessCloseable) {
      allowCloseableModal();
    } else if (!isSuccessCloseable) {
      preventCloseableModal();
    }
  }, [
    allowCloseableModal,
    isCloseable,
    isSuccessCloseable,
    preventCloseableModal,
  ]);

  return (
    <>
      <CloseHeader
        closeScreen={closeModal}
        screen={screenNames.SUCCESS}
        trackingPrefix={state.trackingPrefix}
      />
      <SystemModalContent ref={ref}>
        <Flex
          flexDirection="column"
          mt="minus_3x_big"
          gap="big"
          sx={MODAL_STYLES}
        >
          <Heading
            type={["2", "1"]}
            weight="medium"
            textAlign="center"
            color={titleColor}
          >
            <Glyph
              category="UIActions"
              name="CheckCircle"
              color="positive.50"
              size="xlarge"
            />
            <Box>{title}</Box>
          </Heading>
          <Body
            type="base"
            color="neutral.80"
            weight="normal"
            textAlign="center"
          >
            <Trans
              i18nKey="authentication.emailSentSuccess.firstMessage"
              values={{ email }}
              components={{
                email: <SuccessBodyEmail email={email} />,
              }}
            />
            <Box>{body}</Box>
          </Body>
          <Body
            type="small"
            textAlign="center"
            color="neutral.60"
            weight="normal"
          >
            {footer}
          </Body>
          {isCloseable ? (
            <Box>
              <Button fullWidth type="primary" onClick={closeModal}>
                <Body type="base">{buttonText}</Body>
              </Button>
            </Box>
          ) : null}
        </Flex>
      </SystemModalContent>
    </>
  );
});

export default SuccessBody;
